import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters.ts';
import {
    BILLING_TYPE,
    ORDER_ORGANIZATION_TRANSACTIONAL_ROLE,
    PRODUCT_GROUP_TYPE,
    SummaryOrganization,
    Transaction,
} from '@schuettflix/interfaces';
import { useTranslation } from 'react-i18next';
import { Combobox, LoadingSpinner, Tag } from '@schuettflix/react-components';
import { usePaymentTerms } from '@/clients/finance/usePaymentTerms.ts';
import { getOrganizationTotal } from './utils/getOrganizationTotal.ts';
import { getOrganizationMappedTransactions } from './utils/getOrganizationMappedTransactions.ts';
import { getSelectablePaymentTerms } from './utils/getSelectablePaymentTerms.ts';
import { cn } from '@/shared/utils/cn.ts';
import {
    UpdateOrganizationFinanceSettingsPayload,
    useFinanceSettingsLocalState,
} from './context/FinanceSettingsLocalStateContext.tsx';
import { BILLING_TYPE_LABEL } from '@/shared/components/FinanceSettings/BillingTypeLabels.ts';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';
import { ORGANIZATION_ROLE_LABEL } from '@/shared/components/FinanceSettings/OrganizationRoleLabels.ts';
import { PRODUCT_CATEGORY_LABEL } from '@/phrase/ProductCategoryLabels.ts';
import type { BillingAddress } from '@/clients/organization/types/organization.ts';

type ManualFinanceSettingsProps = {
    showErrorMessages: boolean;
    disabled?: boolean;
    groups?: (SummaryOrganization & {
        orgName: string;
        orgBillingAddress: BillingAddress;
    })[];
    context: 'ORDER' | 'FULFILLMENT' | 'POSITION';
    className?: string;
    pricingStrategy: PRODUCT_GROUP_TYPE;
};

export function ManualFinanceSettings({
    groups = [],
    pricingStrategy,
    showErrorMessages,
    disabled,
    context,
    className,
}: ManualFinanceSettingsProps) {
    const { t } = useTranslation();
    const { formatNumberWithCurrency, formatNumberWithCurrencyPerUnit, formatNumber } = useFormatters();
    const { data: paymentTermsSettings, isLoading: isPaymentTermsSettingsLoading } = usePaymentTerms();
    const financeSettingsLocalState = useFinanceSettingsLocalState();
    const customerBillingCountry = groups?.find(it => it.functionalRoles.includes('CUSTOMER'))?.orgBillingAddress
        ?.country;

    const updateFinanceSettings = (
        billingMethod: BILLING_TYPE,
        paymentTermId: number | null,
        organizationId: number,
        transactionalRole: ORDER_ORGANIZATION_TRANSACTIONAL_ROLE
    ) => {
        let payload: UpdateOrganizationFinanceSettingsPayload;
        switch (billingMethod) {
            case 'CREDIT_NOTE':
            case 'INVOICE':
                if (paymentTermId === null) {
                    paymentTermId =
                        transactionalRole === 'DEBTOR'
                            ? paymentTermsSettings.marketDefaults.revenueIncrease
                            : paymentTermsSettings.marketDefaults.costIncrease;
                }
                payload = {
                    financeSettings: { billingMethod, paymentTermId, transactionalRole },
                    organizationId,
                };
                break;
            case 'PARTNER_INVOICE':
                payload = {
                    financeSettings: { billingMethod, paymentTermId, transactionalRole },
                    organizationId,
                };
                break;
            default:
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw new Error(`Unhandled billing method: ${billingMethod}`);
        }

        return financeSettingsLocalState.updateOrganizationFinanceSettings(payload);
    };

    const getProductCategoriesLabels = (
        transactionalRole: ORDER_ORGANIZATION_TRANSACTIONAL_ROLE,
        mappedTransactions: Map<'DEBTOR' | 'CREDITOR', Transaction[]>
    ) => {
        return [
            ...new Set(mappedTransactions.get(transactionalRole)?.flatMap(({ productCategory }) => productCategory)),
        ]
            .map(role => t(PRODUCT_CATEGORY_LABEL[role]))
            .join(', ');
    };

    if (!paymentTermsSettings) {
        return <LoadingSpinner block />;
    }

    return (
        <div className={cn('mb-2 flex flex-col gap-6', className)} data-test="finance-settings-form">
            {groups.map(group => {
                const total = getOrganizationTotal(group, context);
                const mappedTransactions = getOrganizationMappedTransactions(group);
                const isCustomer = group.functionalRoles[0] === 'CUSTOMER';

                return (
                    <article
                        key={group.orgId}
                        className={cn('flex w-full flex-col gap-2 pb-6 last:pb-0', isCustomer && 'border-b')}
                    >
                        <div className="flex gap-6">
                            <div className="grow truncate">
                                {!isCustomer && (
                                    <p className="font-copy-sm">
                                        {group.functionalRoles.map(role => t(ORGANIZATION_ROLE_LABEL[role])).join(', ')}
                                    </p>
                                )}
                                <h3 className="font-copy-md-strong truncate">{group.orgName}</h3>
                            </div>
                            <div className="shrink-0 self-end">
                                {
                                    <Tag
                                        label={formatNumberWithCurrency(Math.abs(total))}
                                        subdued
                                        type={total < 0 ? 'error' : 'positive'}
                                        className={cn('whitespace-nowrap font-bold', {
                                            '!text-critical': total < 0,
                                            'text-success': total >= 0,
                                        })}
                                    />
                                }
                            </div>
                        </div>
                        <div className="flex flex-col gap-6">
                            {group.financeSettings.map(({ transactionalRole, selected, options }) => {
                                return (
                                    <div className="flex flex-col" key={transactionalRole}>
                                        {pricingStrategy !== 'MERCHANT' && (
                                            <h3 className="font-copy-sm-strong mb-2">
                                                {getProductCategoriesLabels(transactionalRole, mappedTransactions)}
                                            </h3>
                                        )}

                                        <div className="font-copy-md">
                                            <table className="mb-4 w-full">
                                                <tbody>
                                                    {mappedTransactions
                                                        .get(transactionalRole)
                                                        ?.filter(
                                                            transaction =>
                                                                pricingStrategy !== 'MERCHANT' ||
                                                                transaction.productCategory !== 'TRANSPORT'
                                                        )
                                                        ?.map(transaction => (
                                                            <tr key={transaction.productId} className="-mx-1">
                                                                <td className="min-w-[5.5rem] whitespace-nowrap px-1 py-2">
                                                                    {`${formatNumber(transaction.quantity)} ${t(SUPPORTED_UNIT_LABEL[transaction.unit])}`}
                                                                </td>
                                                                <td className="w-full max-w-0 truncate px-1 py-2">
                                                                    {transaction.productName}
                                                                </td>
                                                                <td className="min-w-[6.5rem] whitespace-nowrap px-1 py-2 text-right">
                                                                    {formatNumberWithCurrencyPerUnit(
                                                                        transaction.unitPriceAmount,
                                                                        transaction.unit,
                                                                        transaction.unitPriceCurrency
                                                                    )}
                                                                </td>
                                                                <td
                                                                    className={cn(
                                                                        'min-w-[6.5rem] whitespace-nowrap px-1 py-2 text-right',
                                                                        {
                                                                            'text-success':
                                                                                transactionalRole === 'DEBTOR',
                                                                            'text-critical':
                                                                                transactionalRole !== 'DEBTOR',
                                                                        }
                                                                    )}
                                                                >
                                                                    {formatNumberWithCurrency(
                                                                        transaction.unitPriceAmount *
                                                                            transaction.quantity
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="flex justify-between">
                                            <Combobox
                                                options={
                                                    getSelectablePaymentTerms(
                                                        selected.billingMethod,
                                                        paymentTermsSettings.paymentTerms
                                                    ) || []
                                                }
                                                value={selected.paymentTermId || null}
                                                name={`paymentTerm-${group.orgId}-${transactionalRole}`}
                                                onChange={(paymentTermId: number | null) => {
                                                    updateFinanceSettings(
                                                        selected.billingMethod,
                                                        paymentTermId,
                                                        group.orgId,
                                                        transactionalRole
                                                    );
                                                }}
                                                errorMessage={
                                                    !selected.paymentTermId &&
                                                    selected.billingMethod !== 'PARTNER_INVOICE' &&
                                                    showErrorMessages
                                                        ? t('order.summary.comboboxPlaceholder')
                                                        : undefined
                                                }
                                                placeholder={
                                                    selected.billingMethod !== 'PARTNER_INVOICE'
                                                        ? t('order.summary.comboboxPlaceholder')
                                                        : '-'
                                                }
                                                label={t('orderSummary.paymentTerms')}
                                                className="mr-2 w-full"
                                                variant="md"
                                                disabled={
                                                    selected.billingMethod === 'PARTNER_INVOICE' ||
                                                    isPaymentTermsSettingsLoading ||
                                                    disabled
                                                }
                                                optionsClassName="z-20"
                                            />
                                            <Combobox
                                                options={options.map(option => {
                                                    return {
                                                        value: option.billingMethod,
                                                        label: t(BILLING_TYPE_LABEL[option.billingMethod]),
                                                    };
                                                })}
                                                value={selected.billingMethod || null}
                                                onChange={(billingMethod: BILLING_TYPE | null) => {
                                                    if (!billingMethod) return;
                                                    updateFinanceSettings(
                                                        billingMethod,
                                                        billingMethod === 'PARTNER_INVOICE'
                                                            ? null
                                                            : selected.paymentTermId,
                                                        group.orgId,
                                                        transactionalRole
                                                    );
                                                }}
                                                label={t('orderSummary.billingMethod')}
                                                className="w-full"
                                                disabled={
                                                    isPaymentTermsSettingsLoading ||
                                                    group.orgBillingAddress.country !== customerBillingCountry ||
                                                    disabled
                                                }
                                                variant="md"
                                                optionsClassName="z-20"
                                            />
                                        </div>
                                        {selected.billingMethod === 'PARTNER_INVOICE' && (
                                            <span className="font-copy-sm text-subdued mt-2">
                                                {t('orderSummary.disabledPaymentTerm.hint')}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </article>
                );
            })}
        </div>
    );
}
